import { useTranslation } from 'react-i18next';
import { VictoryLabel } from 'victory';

import { Icon, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import { LineGraphLegend } from '~/modules/artist-roster/components/LineGraph/components/LineGraphLegend';
import { LineGraph } from '~/modules/artist-roster/components/LineGraph/LineGraph';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

interface TrackPreReleaseGraphSectionProps {
  data: { date: string; x: number; y: number }[][];
  targetPercentage?: number;
}

export function TrackPreReleaseGraphSection({
  data,
  targetPercentage = 100,
}: TrackPreReleaseGraphSectionProps) {
  const { t } = useTranslation('roster');

  const LINE_COLORS = ['white', 'oklch(83.9% 0.1673 82.66)'];

  const legendItems = [
    {
      lineColor: LINE_COLORS[0],
      label: t('trackPreReleaseList.benchmarkPanel.graph.legend.newBenchmark'),
    },
    {
      lineColor: LINE_COLORS[1],
      label: t(
        'trackPreReleaseList.benchmarkPanel.graph.legend.defaultBenchmark',
      ),
      dashArray: '2',
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex-col space-y-sm">
          <div className="flex items-center gap-sm">
            <div className="flex items-center gap-xs">
              <div className="font-semibold">
                {t('trackPreReleaseList.benchmarkPanel.adjustBenchmark')}
              </div>

              {
                // @TODO WMG1-494 - Replace with a correct tooltip once UX design is finalized
              }
              <Tooltip
                content={t(
                  'trackPreReleaseList.benchmarkPanel.adjustBenchmark',
                )}
                size="sm"
                side="top"
                delayDuration={0}
                sideOffset={8}
                align="start"
                contentClassName="max-w-[300px] leading-4"
              >
                <Icon type="info-circle" variant="light" size="xs" />
              </Tooltip>
            </div>
          </div>

          <LineGraphLegend items={legendItems} />
        </div>
        <div>
          {
            // @TODO: WMG1-318 Add functionality to reset the benchmark
          }
          <Button variant="tertiary" size="small" className="font-semibold">
            {t('trackPreReleaseList.benchmarkPanel.graph.resetBenchmark')}
          </Button>
        </div>
      </div>
      <div className="h-xl">
        <LineGraph
          targetPercentage={targetPercentage}
          dataLabelText={t(
            'trackPreReleaseList.benchmarkPanel.graph.dataLabel',
          )}
          streamType="benchmarkStreams"
          lineColors={LINE_COLORS}
          xAxisProps={{
            style: {
              tickLabels: {
                textAlign: 'left',
              },
            },
            domain: [0, data[0].length],
            tickLabelComponent: <VictoryLabel textAnchor="start" />,
            tickValues: data[0].map((datum) => datum.x),
            tickFormat: data[0].map((datum) =>
              t('trackPreReleaseList.benchmarkPanel.graph.monthWithNumber', {
                monthNumber: datum.date,
              }),
            ),
          }}
          yAxisProps={{
            orientation: 'left',
            label: t('trackDetails.labels.streamsRTD'),
            tickFormat: (num) => formatMillionsNumber(num, true),
            style: {
              axisLabel: {
                // Flip label direction
                angle: -90,
                padding: 25,
              },
            },
            tickLabelComponent: (
              <VictoryLabel
                backgroundStyle={
                  {
                    fill: '#232323',
                    rx: 5,
                    ry: 5,
                  } as React.CSSProperties
                }
                backgroundPadding={3}
              />
            ),
          }}
          data={data}
          containerClassName="w-full"
        />
      </div>
    </>
  );
}

TrackPreReleaseGraphSection.displayName = 'TrackPreReleaseGraphSection';
